import { formatBytes } from "./archive-ingest";
import { isCapabilitySurfaceRule } from "./scoring";
import type { assessJarIntegrity } from "./integrity";
import type { lookupOfficialModrinthFile } from "./official-reputation";
import type {
  ArchiveEntry,
  ClassInfo,
  Finding,
  Indicator,
  Severity,
} from "./types";
import type { ThreatContext } from "./threat-intel";

/**
 * The behaviour rules: what a scan looks for, and how a hit is described.
 *
 * These were the largest single block inside `analyzer.ts` and had no
 * business being there. A rule is data -- an id, a severity, a category, some
 * patterns -- plus one helper that turns matching values into evidence. None of
 * it touches the scan lifecycle, the archive walk or the report, so keeping it
 * beside them meant that adding a rule meant editing a 4,000-line function
 * that also assembled the findings those rules produce.
 *
 * The ids are grouped by family (`JARLAB-CRED-*`, `JARLAB-EXEC-*`,
 * `JARLAB-OBF-*`, `JARLAB-EVASION-*`, `JARLAB-PERSIST-*`, `JARLAB-SIGN-*`,
 * `JARLAB-DEP-*`), which is the only taxonomy here: there is one table and
 * `analyzeJar` walks it once, per detection context.
 *
 * The block was moved verbatim. Nothing about a rule's meaning, pattern,
 * severity or evidence formatting changed in this split.
 */

/** The per-entry detection context a rule is matched against. */
export type DetectionContext = ThreatContext;

export interface BehaviorRule {
  id: string;
  severity: Severity;
  confidence: Finding["confidence"];
  category: Finding["category"];
  title: string;
  description: string;
  recommendation: string;
  tags: string[];
  patterns: RegExp[];
}

export const BEHAVIOR_RULES: BehaviorRule[] = [
  {
    id: "JARLAB-CRED-001",
    severity: "high",
    confidence: "high",
    category: "credentials",
    title: "Credential-store access",
    description:
      "The sample references browser or application credential stores commonly targeted by stealers.",
    recommendation:
      "Review the surrounding call path and verify whether access is necessary for the claimed functionality.",
    tags: ["credential-access", "stealer"],
    // "Cookies" needs a browser-profile path around it. On its own the word
    // matched Minecraft's foods/cookies item tag and reported a high-severity
    // credential finding against Fabric API.
    patterns: [
      /Login Data/i,
      /Local State/i,
      /logins\.json/i,
      /key4\.db/i,
      /\\Cookies\b/i,
      /\bcookies\.sqlite\b/i,
      /(?:User Data|AppData|Roaming|Application Support)[\\/][^"'\s]{0,80}Cookies\b/i,
      /Web Data/i,
      /wallet\.dat/i,
    ],
  },
  {
    id: "JARLAB-CRED-002",
    severity: "high",
    confidence: "high",
    category: "credentials",
    title: "Discord session material access",
    description:
      "The sample references Discord storage or token patterns associated with session theft.",
    recommendation:
      "Treat token collection as hostile unless the package has a clearly documented security purpose.",
    tags: ["discord", "token-theft"],
    patterns: [
      /discord(?:canary|ptb)?[\/\\]Local Storage/i,
      /dQw4w9WgXcQ:[A-Za-z0-9_\-=]+/i,
      /mfa\.[A-Za-z0-9_-]{40,}/,
      /[\w-]{24,26}\.[\w-]{6}\.[\w-]{25,110}/,
    ],
  },
  {
    id: "JARLAB-EXEC-001",
    severity: "high",
    confidence: "high",
    category: "execution",
    title: "Operating-system command execution",
    description:
      "The sample can spawn a process or invoke a platform command shell.",
    recommendation:
      "Inspect every caller and command string. Unexpected process execution is a strong compromise signal.",
    tags: ["process", "command"],
    patterns: [
      /java[./]lang[./]Runtime\.exec/i,
      /java[./]lang[./]ProcessBuilder/i,
      /\bpowershell(?:\.exe)?\b/i,
      /\bcmd(?:\.exe)?\s+\/c\b/i,
      /\/bin\/(?:ba)?sh\b/i,
    ],
  },
  {
    id: "JARLAB-NET-001",
    severity: "medium",
    confidence: "medium",
    category: "network",
    title: "Outbound network capability",
    description:
      "The sample contains APIs capable of making outbound HTTP or socket connections.",
    recommendation:
      "Correlate endpoints with the package’s documented service and inspect data sent over the connection.",
    tags: ["network", "c2"],
    patterns: [
      /java[./]net[./]URL\.(?:openConnection|openStream)/i,
      /java[./]net[./]HttpURLConnection\.(?:connect|getInputStream|getOutputStream)/i,
      /java[./]net[./](?:Socket|DatagramSocket)\.(?:<init>|connect|send|getInputStream|getOutputStream)/i,
      /java[./]net[./]http[./]HttpClient\.(?:send|sendAsync)/i,
      /okhttp3[./](?:OkHttpClient\.newCall|Call\.execute)/i,
      /org[./]apache[./]http[./]client[^\n]{0,120}\.(?:execute|open)/i,
    ],
  },
  {
    id: "JARLAB-LOAD-001",
    severity: "high",
    confidence: "high",
    category: "execution",
    title: "Dynamic bytecode loading",
    description:
      "The sample can load classes from bytes or external locations at runtime.",
    recommendation:
      "Trace the source of loaded bytes. Remote or encrypted class material is a common loader technique.",
    tags: ["classloader", "loader"],
    // A bare URLClassLoader reference also covers code that only reads an
    // existing loader's URLs. Construction and loadClass are the operations
    // that actually pull in new code.
    patterns: [
      /URLClassLoader[./](?:<init>|loadClass|newInstance)/i,
      /new\s+URLClassLoader/i,
      /ClassLoader\.defineClass/i,
      /MethodHandles\$Lookup\.defineClass/i,
      /Unsafe\.defineClass/i,
    ],
  },
  {
    id: "JARLAB-NATIVE-001",
    severity: "high",
    confidence: "medium",
    category: "execution",
    title: "Native code loading",
    description:
      "The sample can load native libraries, moving behavior outside Java’s normal visibility.",
    recommendation:
      "Extract and inspect every bundled native binary in a dedicated reverse-engineering environment.",
    tags: ["jni", "native"],
    patterns: [
      /java[./]lang[./]System\.load(?:Library)?/i,
      /com[./]sun[./]jna/i,
      /org[./]scijava[./]nativelib/i,
    ],
  },
  {
    id: "JARLAB-COLLECT-001",
    severity: "high",
    confidence: "medium",
    category: "collection",
    title: "Screen, input, or clipboard collection",
    description:
      "The sample references desktop automation or clipboard interfaces that can capture user activity.",
    recommendation:
      "Confirm that screen, keyboard, mouse, or clipboard access is an explicit product feature.",
    tags: ["collection", "surveillance"],
    patterns: [
      /java[./]awt[./]Robot/i,
      /getSystemClipboard/i,
      /createScreenCapture/i,
      /KeyEventDispatcher/i,
    ],
  },
  {
    id: "JARLAB-PERSIST-001",
    severity: "high",
    confidence: "medium",
    category: "persistence",
    title: "Persistence mechanism",
    description:
      "The sample contains strings or APIs associated with automatic execution after reboot or login.",
    recommendation:
      "Inspect installation paths and startup modifications. Persistence is rarely appropriate for a mod or plugin.",
    tags: ["startup", "persistence"],
    patterns: [
      /CurrentVersion[\\/]Run/i,
      /schtasks(?:\.exe)?\s+\/create/i,
      /\\Start Menu\\Programs\\Startup/i,
      /LaunchAgents/i,
      /\bcrontab\b/i,
    ],
  },
  {
    id: "JARLAB-EVASION-001",
    severity: "low",
    confidence: "medium",
    category: "evasion",
    title: "Reflective dispatch",
    description:
      "The sample resolves classes or members by name at runtime, which can hide part of the real call graph from simple inspection. Reflection is also routine in ordinary libraries, so this is context rather than a verdict on its own.",
    recommendation:
      "Decompile the flagged classes and resolve reflected class and method names, especially where the name is built from a decoded string.",
    tags: ["reflection", "evasion"],
    // Matching the java.lang.reflect package caught every method that merely
    // returns a Method or builds an array, which is normal library code. Only
    // the calls that actually dispatch by name belong here.
    patterns: [
      /Class\.forName/i,
      /Method\.invoke/i,
      /Constructor\.newInstance/i,
      /setAccessible/i,
      /Class\.getDeclaredMethods?\b/i,
      /Class\.getMethods?\(/i,
    ],
  },
  {
    id: "JARLAB-EVASION-002",
    severity: "medium",
    confidence: "low",
    category: "evasion",
    title: "Anti-analysis checks",
    description:
      "The sample checks for sandboxes, debuggers, virtual machines, or analysis tooling.",
    recommendation:
      "Review conditional branches around these checks and treat evasive execution paths as high priority.",
    tags: ["anti-vm", "anti-debug"],
    patterns: [
      /wireshark|processhacker|procmon|fiddler|x64dbg/i,
      /VirtualBox|VMware|QEMU|Hyper-V/i,
      /isDebuggerConnected|java\.vm\.info/i,
    ],
  },
  {
    id: "JARLAB-MC-001",
    severity: "medium",
    confidence: "medium",
    category: "credentials",
    title: "Minecraft account data access",
    description:
      "The sample references launcher account, session, or authentication storage.",
    recommendation:
      "Verify why the package needs launcher credentials or session tokens.",
    tags: ["minecraft", "session"],
    patterns: [
      /launcher_accounts\.json/i,
      /launcher_profiles\.json/i,
      /accessToken/i,
      /sessionserver\.mojang\.com/i,
      /minecraftservices\.com/i,
    ],
  },
  {
    id: "JARLAB-CRED-003",
    severity: "high",
    confidence: "high",
    category: "credentials",
    title: "Cryptocurrency wallet collection",
    description:
      "The sample references browser wallet extensions, desktop wallet stores, or seed and key material commonly harvested by stealers.",
    recommendation:
      "Trace every wallet path and archive operation. A Minecraft mod has no legitimate reason to enumerate unrelated wallets.",
    tags: ["wallet", "stealer", "cryptocurrency"],
    // Phantom, Exodus, Ronin and Atomic are ordinary English words before they
    // are wallet brands — "phantom" in Eclipse's resource model was enough to
    // report wallet theft. Those names need a wallet nearby; the coined brand
    // names do not.
    patterns: [
      /\b(?:MetaMask|Electrum|Coinomi|Keplr|Solflare)\b/i,
      /\b(?:Phantom|Exodus|Ronin|Atomic)\b[^\n]{0,40}\bwallets?\b/i,
      /\bwallets?\b[^\n]{0,40}\b(?:Phantom|Exodus|Ronin|Atomic)\b/i,
      /\b(?:seed phrase|mnemonic phrase|wallet seed|private key)\b/i,
      /nkbihfbeogaeaoehlefnkodbefgpgknn/i,
      /ibnejdfjmmkpcnlpebklmnkoeoihofec/i,
      /wallets?[\\/].*(?:json|dat|ldb)/i,
    ],
  },
  {
    id: "JARLAB-CRED-004",
    severity: "high",
    confidence: "medium",
    category: "credentials",
    title: "Browser-secret decryption capability",
    description:
      "The sample references Windows DPAPI, Chromium encryption keys, or browser databases used to decrypt stored credentials and cookies.",
    recommendation:
      "Inspect the caller and all SQLite queries. Correlate this with network or archive-writing behavior.",
    tags: ["browser", "dpapi", "credential-access"],
    patterns: [
      /\bCryptUnprotectData\b/i,
      /\bDPAPI\b/i,
      /\bos_crypt\b/i,
      /\bencrypted_key\b/i,
      /\b(?:logins|cookies|history)\b.*\bSQLite\b/i,
    ],
  },
  {
    id: "JARLAB-MC-002",
    severity: "high",
    confidence: "medium",
    category: "credentials",
    title: "Live Minecraft session extraction",
    description:
      "The sample references active Minecraft session getters and access-token material that can enable account hijacking.",
    recommendation:
      "Confirm whether the code exports the access token, UUID, or username outside the running client.",
    tags: ["minecraft", "session", "token-theft"],
    patterns: [
      /MinecraftClient[./](?:getInstance|getSession)/i,
      /Session[./]getAccessToken/i,
      /\bgetAccessToken\b/i,
      /\bmcUuid\b/i,
      /\bmcUsername\b/i,
      /\bsubmitMinecraftLog\b/i,
    ],
  },
  {
    id: "JARLAB-MC-003",
    severity: "critical",
    confidence: "high",
    category: "credentials",
    title: "Live Minecraft session serialized for exfiltration",
    description:
      "The same payload template serializes a player identity, UUID, server, and live session token for outbound delivery.",
    recommendation:
      "Treat this as account theft. Quarantine the JAR and revoke active Minecraft and Microsoft sessions from a clean device.",
    tags: ["minecraft", "session-token", "exfiltration", "stealer"],
    patterns: [
      /"username"\s*:\s*"%s"[\s\S]{0,500}"uuid"\s*:\s*"%s"[\s\S]{0,500}"server"\s*:\s*"%s"[\s\S]{0,700}"token"\s*:\s*"%s"/i,
      /"minecraftInfo"\s*:\s*\{[\s\S]{0,400}"username"\s*:[\s\S]{0,400}"uuid"\s*:[\s\S]{0,400}"accessToken"\s*:/i,
    ],
  },
  {
    id: "JARLAB-EVASION-003",
    severity: "critical",
    confidence: "high",
    category: "evasion",
    title: "Security-product tampering",
    description:
      "The sample contains commands used to weaken Microsoft Defender, firewall, or security policy controls.",
    recommendation:
      "Do not run the sample. Review host Defender exclusions and security-policy changes from a trusted environment.",
    tags: ["defender", "tampering", "security-control"],
    patterns: [
      /Add-MpPreference\s+-ExclusionPath/i,
      /Set-MpPreference\s+-DisableRealtimeMonitoring/i,
      /\bsc(?:\.exe)?\s+(?:stop|config)\s+(?:WinDefend|Sense)\b/i,
      /\bnetsh\s+advfirewall\s+set\b/i,
      /DisableAntiSpyware/i,
    ],
  },
  {
    id: "JARLAB-PERSIST-002",
    severity: "high",
    confidence: "high",
    category: "persistence",
    title: "Scheduled or scripted autostart",
    description:
      "The sample creates a scheduled task, login script, or registry-run entry for continued execution.",
    recommendation:
      "Review the task name, command, trigger, and copied payload path. Remove persistence before reconnecting an affected host.",
    tags: ["scheduled-task", "registry-run", "persistence"],
    patterns: [
      /\bschtasks(?:\.exe)?\b/i,
      /\bTaskScheduler\b/i,
      /WScript\.Shell/i,
      /Updater\.vbs/i,
      /Software[\\/]Microsoft[\\/]Windows[\\/]CurrentVersion[\\/]Run/i,
    ],
  },
  {
    id: "JARLAB-COLLECT-002",
    severity: "high",
    confidence: "high",
    category: "collection",
    title: "Keylogging, webcam, or audio surveillance",
    description:
      "The sample references global input hooks, webcam capture, microphone capture, or continuous screen streaming.",
    recommendation:
      "Treat covert capture as hostile. Identify affected devices and rotate credentials from a separate clean system.",
    tags: ["keylogger", "webcam", "surveillance"],
    patterns: [
      /NativeKeyListener/i,
      /KeyLoggingHandler/i,
      /WebcamShareHandler/i,
      /ScreenShareHandler/i,
      /com[./]github[./]sarxos[./]webcam/i,
      /TargetDataLine/i,
      /AudioSystem[./]getLine/i,
      /SetWindowsHookEx/i,
    ],
  },
  {
    id: "JARLAB-NET-002",
    severity: "critical",
    confidence: "high",
    category: "network",
    title: "Known stealer exfiltration route",
    description:
      "The sample contains a request path or header used by active Minecraft credential-stealing infrastructure.",
    recommendation:
      "Quarantine the sample and search proxy, DNS, and endpoint logs for the matched route.",
    tags: ["exfiltration", "c2", "minecraft"],
    patterns: [
      /\/shard\/prefireMc\b/i,
      /\/shard\/submitMinecraftLog\b/i,
      /\bX-Runtime-Env\b/i,
      /\bx-cdn-origin-verify\b/i,
    ],
  },
  {
    id: "JARLAB-NET-003",
    severity: "medium",
    confidence: "medium",
    category: "network",
    title: "Blockchain dead-drop resolution",
    description:
      "The sample references blockchain JSON-RPC calls that can be used to resolve rotating command-and-control infrastructure.",
    recommendation:
      "Inspect the contract address, function selector, and decoded return value. Legitimate blockchain clients can use the same APIs.",
    tags: ["etherhiding", "blockchain", "c2"],
    patterns: [
      /"method"\s*:\s*"eth_call"/i,
      /\beth_call\b/i,
      /\beth_getCode\b/i,
      /\beth_getStorageAt\b/i,
      /\b(?:polygon|matic).*(?:rpc|contract)\b/i,
    ],
  },
  {
    id: "JARLAB-NET-004",
    severity: "critical",
    confidence: "high",
    category: "network",
    title: "WeedHack staged-delivery route",
    description:
      "The sample contains delivery and module routes used by WeedHack loaders to retrieve later stages.",
    recommendation:
      "Quarantine the archive and search DNS, proxy, and endpoint telemetry for the recovered route and child Java processes.",
    tags: ["weedhack", "c2", "delivery", "second-stage"],
    patterns: [
      /\/api\/delivery\/handler\b/i,
      /\/files\/jar\/module\b/i,
    ],
  },
  {
    id: "JARLAB-EXEC-002",
    severity: "high",
    confidence: "high",
    category: "execution",
    title: "Known staged-payload artifact",
    description:
      "The sample references a staged executable or script name associated with active Minecraft malware delivery chains.",
    recommendation:
      "Trace the downloaded bytes, destination path, and process launch. Analyze every dropped payload separately.",
    tags: ["dropper", "second-stage", "execution"],
    patterns: [
      /\bPjibf\.exe\b/i,
      /\belevator\.jar\b/i,
      /\bSecurityManager\.jar\b/i,
      /\bcomponent-[0-9a-f-]+\.jar\b/i,
      /\bUpdater\.vbs\b/i,
      /\bmain\.py\b.*--prefireId/i,
    ],
  },
  {
    id: "JARLAB-EVASION-004",
    severity: "high",
    confidence: "medium",
    category: "evasion",
    title: "JNIC native bytecode protection",
    description:
      "The archive uses JNIC-style native stubs and resources, which can conceal Java behavior inside platform-specific binaries.",
    recommendation:
      "Hash and inspect every JNIC resource. Do not treat a nearly empty Java decompilation as evidence of safety.",
    tags: ["jnic", "native", "obfuscation"],
    patterns: [
      /dev[./]jnic(?:[./]|$)/i,
      /\$jnicLoader\b/i,
      /JNICLoader/i,
      /dev[./]jnic[./]lib[./].+\.dat/i,
    ],
  },
];

export const FAMILY_MARKERS: Array<[RegExp, string]> = [
  [/\bfractureiser\b/i, "Possible Fractureiser"],
  [/\bskuld\b/i, "Possible Skuld"],
  [/\bblank(?:\s|-)?grabber\b/i, "Possible Blank Grabber"],
  [/\blunar(?:\s|-)?grabber\b/i, "Possible Lunar Grabber"],
  [/\braven(?:\s|-)?stealer\b/i, "Possible Raven Stealer"],
  [/\bdiscordrat\b/i, "Possible Discord RAT"],
  [/\bjrat\b/i, "Possible jRAT"],
];


export function evidenceFor(rule: BehaviorRule, values: string[]) {
  const hits: string[] = [];
  for (const value of values) {
    for (const pattern of rule.patterns) {
      const match = value.match(pattern);
      if (match) {
        const clipped =
          value.length > 180 ? `${value.slice(0, 177).trim()}…` : value.trim();
        if (clipped && !hits.includes(clipped)) hits.push(clipped);
        break;
      }
    }
    if (hits.length >= 4) break;
  }
  return hits;
}

// ---------------------------------------------------------------------------
// The second category: rules whose condition is a scan, not a pattern.
//
// Everything above is a `BehaviorRule`: a list of patterns matched against one
// detection context. The rules below are the other half of the vocabulary --
// a condition over the finished scan (integrity result, decode counts, IOC
// extraction, the entry list) whose evidence has to be computed rather than
// matched. They used to live inline in the report-assembly function, which is
// how half the rule ids ended up discoverable only by reading a thousand lines
// of assembly. They are here now, next to the table, with their text and their
// condition together.
//
// They are emitted by named functions rather than held in an array because
// **emission order is part of the report contract**: findings are numbered in
// insertion order and the tests compare reports byte for byte. A table would
// have forced a sort key that encodes scan order, which is a worse thing to
// have to maintain than a sequence of calls that says what it does.
// `assembleFindings` calls these in the order the scan produces them.
// ---------------------------------------------------------------------------

/** One finding, ready to be numbered. `id` is the rule id the report shows. */
export type FindingSpec = Omit<BehaviorRule, "patterns"> & {
  evidence: string[];
};

/** Where a rule sends a match. Assembly owns the numbering and the dedupe. */
export type RuleSink = (rule: FindingSpec, entry: string) => void;

type JarIntegrityResult = Awaited<ReturnType<typeof assessJarIntegrity>>;
type OfficialFileResult = Awaited<ReturnType<typeof lookupOfficialModrinthFile>>;

/** Per-source values, for the rules that read a source's own content. */
export function valuesByEntry(
  contexts: DetectionContext[],
): Map<string, string[]> {
  const byEntry = new Map<string, string[]>();
  for (const context of contexts) {
    const values = byEntry.get(context.source) ?? [];
    values.push(...context.values);
    byEntry.set(context.source, values);
  }
  return byEntry;
}

/**
 * Rule ids per entry, excluding whole-archive identity matches.
 *
 * `JARLAB-HASH-*` and `JARLAB-FAMILY-*` are deliberately left out: they say
 * what a sample *is*, not what it does, so counting them toward a same-source
 * correlation would let one known sample manufacture a critical finding.
 */
export function behaviorByEntry(
  findings: Finding[],
): Map<string, Set<string>> {
  const byEntry = new Map<string, Set<string>>();
  for (const finding of findings) {
    if (
      finding.ruleId.startsWith("JARLAB-HASH-") ||
      finding.ruleId.startsWith("JARLAB-FAMILY-")
    ) {
      continue;
    }
    const ids = byEntry.get(finding.entry) ?? new Set<string>();
    ids.add(finding.ruleId);
    byEntry.set(finding.entry, ids);
  }
  return byEntry;
}

/**
 * Signed-archive rules. `JARLAB-SIGN-002` fires when an entry no longer matches
 * the digest the signed manifest recorded; `JARLAB-SIGN-003` when only part of
 * the signature metadata is present. Mutually exclusive, so they are one rule
 * with two arms rather than two rules.
 */
export function emitIntegrityFindings(
  integrity: JarIntegrityResult,
  add: RuleSink,
) {
if (integrity.status === "tampered") {
  add(
    {
      id: "JARLAB-SIGN-002",
      severity: "critical",
      confidence: "high",
      category: "signature",
      title: "Signed JAR contents were modified",
      description:
        "One or more current archive entries do not match the digest recorded in the signed manifest.",
      recommendation:
        "Reject this copy and obtain the release again from the publisher. Compare hashes before any execution.",
      tags: ["signature", "tampering", "supply-chain"],
      evidence: [
        ...integrity.invalidEntries
          .slice(0, 8)
          .map(
            (entry) =>
              `${entry.path}: expected ${entry.expected}, got ${entry.actual}`,
          ),
        ...integrity.missingEntries
          .slice(0, 4)
          .map((path) => `Signed entry missing: ${path}`),
      ],
    },
    "META-INF/MANIFEST.MF",
  );
} else if (integrity.status === "incomplete") {
  add(
    {
      id: "JARLAB-SIGN-003",
      severity: "medium",
      confidence: "high",
      category: "signature",
      title: "Incomplete signature metadata",
      description:
        "The archive contains only part of the metadata required for a complete signed JAR.",
      recommendation:
        "Compare this copy with the publisher release and verify it with the JDK jarsigner tool.",
      tags: ["signature", "integrity"],
      evidence: integrity.notes,
    },
    "META-INF",
  );
}
}

/**
 * Provenance echo for an exact Modrinth release match. Not a detection: it
 * exists so an analyst reading a hostile-looking archive knows the bytes match
 * something they published.
 */
export function emitReputationFinding(
  input: { officialFile: OfficialFileResult; analyzedFileName: string },
  add: RuleSink,
) {
if (input.officialFile.status === "matched") {
  add(
    {
      id: "JARLAB-REPUTATION-MODRINTH",
      severity: "info",
      confidence: "high",
      category: "signature",
      title: "Exact official Modrinth release match",
      description:
        "The analyzed SHA-512 exactly matches a file published through Modrinth.",
      recommendation:
        "Use the identity match as provenance evidence, while still reviewing behaviors that are risky for the claimed function.",
      tags: ["official-release", "modrinth", "provenance"],
      evidence: [
        input.officialFile.projectTitle ?? input.officialFile.projectId ?? "Modrinth project",
        input.officialFile.versionNumber ?? input.officialFile.versionName ?? "Published version",
        input.officialFile.officialFileName ?? input.analyzedFileName,
      ],
    },
    "official release lookup",
  );
}
}

/**
 * Bulk string-encryption rules: `JARLAB-OBF-005` (XOR packets), `-008`
 * (Unicode character arrays), `-006` (call-site-dependent keys) and `-007`
 * (invokedynamic metadata). All four ask the same question of a different
 * decoder, so their thresholds sit side by side and can be compared.
 */
export function emitStringEncryptionFindings(
  input: {
    decodedValues: {
      xorBySource: Map<string, number>;
      characterPackedBySource: Map<string, number>;
    };
    staticRecoveryBySource: Map<string, Map<string, number>>;
  },
  add: RuleSink,
) {
for (const [entry, count] of input.decodedValues.xorBySource) {
  // This rule is about a class's constant pool. Reporting it against a
  // resource meant flagging localization files as string encryption.
  if (count < 8 || !entry.toLowerCase().endsWith(".class")) continue;
  add(
    {
      id: "JARLAB-OBF-005",
      severity: count >= 32 ? "high" : "medium",
      confidence: "high",
      category: "evasion",
      title: "Bulk runtime string decryption",
      description:
        "The class stores strings as keyed XOR packets and rebuilds them at runtime, concealing URLs, commands, paths, and configuration from ordinary string scans.",
      recommendation:
        "Prioritize the recovered strings and their callers. Correlate decoded endpoints with process execution and downloaded payload handling.",
      tags: ["obfuscation", "string-encryption", "xor"],
      evidence: [
        `${count.toLocaleString()} XOR-packed constants recovered`,
        "Format: key length + key bytes + XOR payload",
      ],
    },
    entry,
  );
}

for (const [entry, count] of input.decodedValues.characterPackedBySource) {
  if (count < 8 || !entry.toLowerCase().endsWith(".class")) continue;
  add(
    {
      id: "JARLAB-OBF-008",
      severity: count >= 32 ? "high" : "medium",
      confidence: "high",
      category: "evasion",
      title: "Bulk Unicode character-array decryption",
      description:
        "The class hides strings in Unicode character arrays with embedded prefix or suffix keys and reconstructs them with XOR or subtraction at runtime.",
      recommendation:
        "Review the recovered endpoints, credential fields, payload paths, and process arguments with their reachable callers.",
      tags: ["obfuscation", "string-encryption", "unicode"],
      evidence: [
        `${count.toLocaleString()} character-packed constants recovered`,
        "Prefix/suffix key layouts with XOR and subtraction were evaluated",
        "No sample code was executed",
      ],
    },
    entry,
  );
}

for (const [entry, transforms] of input.staticRecoveryBySource) {
  const rotating = transforms.get("call-site rotating XOR") ?? 0;
  const bootstrap =
    transforms.get("invokedynamic metadata XOR") ?? 0;
  const rebuilt = transforms.get("static fragment rebuild") ?? 0;
  if (rotating >= 2) {
    add(
      {
        id: "JARLAB-OBF-006",
        severity: rotating >= 16 ? "high" : "medium",
        confidence: "high",
        category: "evasion",
        title: "Call-site-dependent string encryption",
        description:
          "JarLab statically evaluated a rotating-XOR decoder whose key depends on the caller class, a technique used to conceal URLs, payload routes, commands, and campaign identifiers.",
        recommendation:
          "Inspect the recovered strings and their call sites. Prioritize delivery routes, blockchain contracts, process arguments, and credential fields.",
        tags: ["obfuscation", "string-encryption", "static-evaluation"],
        evidence: [
          `${rotating.toLocaleString()} call-site strings recovered`,
          `${rebuilt.toLocaleString()} split values rebuilt`,
          "No sample code was executed",
        ],
      },
      entry,
    );
  }
  if (bootstrap >= 4) {
    add(
      {
        id: "JARLAB-OBF-007",
        severity: "medium",
        confidence: "high",
        category: "evasion",
        title: "Encrypted invokedynamic call metadata",
        description:
          "Method owners, names, and descriptors are XOR-encrypted inside invokedynamic bootstrap arguments to hide the real call graph.",
        recommendation:
          "Use the recovered bootstrap metadata with the decompiled source and bytecode view to resolve hidden method calls.",
        tags: ["obfuscation", "invokedynamic", "call-graph"],
        evidence: [
          `${bootstrap.toLocaleString()} bootstrap descriptors recovered`,
          "Owner, method, and JVM descriptor fields reconstructed",
        ],
      },
      entry,
    );
  }
}
}

/**
 * `JARLAB-STRUCT-003`: fabric metadata claiming a well-known optimisation mod
 * while loading unrelated obfuscated entry points.
 */
export function emitMasqueradeFinding(
  valuesByEntry: Map<string, string[]>,
  add: RuleSink,
) {
for (const [entry, values] of valuesByEntry) {
  if (!/fabric\.mod\.json$/i.test(entry)) continue;
  const metadata = values.join("\n");
  const claimedProject = metadata.match(
    /"(?:id|name)"\s*:\s*"(immediatelyfast|dynamic\s*fps)"/i,
  )?.[1];
  const foreignEntryPoint = metadata.match(
    /(?:skid[./]gypsyy|q3r[./]d4526l[./]f1a7)[A-Za-z0-9_.$/]*/i,
  )?.[0];
  if (!claimedProject || !foreignEntryPoint) continue;
  add(
    {
      id: "JARLAB-STRUCT-003",
      severity: "high",
      confidence: "high",
      category: "structure",
      title: "Trusted mod identity impersonation",
      description:
        "Fabric metadata claims a well-known optimization mod while loading unrelated, obfuscated entry-point packages.",
      recommendation:
        "Compare the archive with the publisher’s signed or official release and treat this copy as trojanized.",
      tags: ["masquerading", "supply-chain", "minecraft"],
      evidence: [
        `Claimed project: ${claimedProject}`,
        `Unexpected entry point: ${foreignEntryPoint}`,
      ],
    },
    entry,
  );
}
}

/**
 * Correlations scoped to a single source: `JARLAB-COMPOUND-003` and `-004` pair
 * two capabilities that co-occur in one class, and `-005` is the staged
 * dropper chain. Same-source is what makes these trustworthy enough to be
 * critical, so they are evaluated per entry rather than archive-wide.
 */
export function emitEntryCorrelations(
  input: {
    behaviorByEntry: Map<string, Set<string>>;
    valuesByEntry: Map<string, string[]>;
  },
  add: RuleSink,
) {
for (const [entry, ids] of input.behaviorByEntry) {
  const credentialAccess = [
    "JARLAB-CRED-001",
    "JARLAB-CRED-002",
    "JARLAB-CRED-003",
    "JARLAB-CRED-004",
    "JARLAB-MC-001",
    "JARLAB-MC-002",
    "JARLAB-MC-003",
  ].some((id) => ids.has(id));
  const outbound = ["JARLAB-NET-001", "JARLAB-NET-002"].some((id) =>
    ids.has(id),
  );
  if (credentialAccess && outbound) {
    add(
      {
        id: "JARLAB-COMPOUND-003",
        severity: "critical",
        confidence: "high",
        category: "credentials",
        title: "Collection and exfiltration in the same class",
        description:
          "The same class or resource contains credential-collection and outbound-network evidence, substantially reducing the chance of an archive-wide false correlation.",
        recommendation:
          "Trace this class first. Quarantine the sample unless the complete data flow is demonstrably benign.",
        tags: ["same-source", "stealer", "exfiltration"],
        evidence: Array.from(ids).slice(0, 8),
      },
      entry,
    );
  }
  if (
    ids.has("JARLAB-EVASION-003") &&
    (ids.has("JARLAB-PERSIST-001") ||
      ids.has("JARLAB-PERSIST-002"))
  ) {
    add(
      {
        id: "JARLAB-COMPOUND-004",
        severity: "critical",
        confidence: "high",
        category: "evasion",
        title: "Security tampering with persistence",
        description:
          "Security-product tampering and autostart behavior occur in the same source, a strong malware installation pattern.",
        recommendation:
          "Do not run the sample. Inspect Defender exclusions, scheduled tasks, and registry-run keys on exposed hosts.",
        tags: ["same-source", "tampering", "persistence"],
        evidence: Array.from(ids).slice(0, 8),
      },
      entry,
    );
  }

  const sourceValues = input.valuesByEntry.get(entry) ?? [];
  const stagedSignals = [
    {
      label: "encrypted payload route",
      patterns: [/\/cdn\/[a-z0-9/_-]{8,}/i],
    },
    {
      label: "AES payload decryption",
      patterns: [/AES\/CBC/i, /javax[./]crypto[./]Cipher\.doFinal/i],
    },
    {
      label: "ZIP payload extraction",
      patterns: [/java[./]util[./]zip[./]ZipInputStream/i],
    },
    {
      label: "Python second stage",
      patterns: [/\bmain\.py\b/i, /\bpython(?:w)?\.exe\b/i],
    },
    {
      label: "portable runtime staging",
      patterns: [/\bjre-embedded\b/i],
    },
  ].filter((signal) =>
    sourceValues.some((value) =>
      signal.patterns.some((pattern) => pattern.test(value)),
    ),
  );
  if (
    ids.has("JARLAB-EXEC-001") &&
    outbound &&
    stagedSignals.length >= 4
  ) {
    add(
      {
        id: "JARLAB-COMPOUND-005",
        severity: "critical",
        confidence: "high",
        category: "execution",
        title: "Encrypted staged-payload downloader",
        description:
          "One class resolves an outbound payload route, decrypts and extracts the response, then starts a separate runtime. This is a high-confidence dropper chain.",
        recommendation:
          "Quarantine the JAR and inspect any host where it ran for the recovered runtime directory, Python process, downloaded archive, and child-process logs.",
        tags: ["same-source", "dropper", "encrypted-payload", "second-stage"],
        evidence: stagedSignals.map((signal) => signal.label),
      },
      entry,
    );
  }
}
}

/**
 * `JARLAB-SECRET-001`: a live-looking webhook or bot credential in a reachable
 * source. Derived from IOC extraction rather than from pattern rules, which is
 * why it is a scan rule and not a `BEHAVIOR_RULES` entry.
 */
export function emitExfiltrationFinding(
  input: { indicators: Indicator[]; entryIsReachable: (entry: string) => boolean },
  add: RuleSink,
) {
  const webhookEntries = Array.from(
    new Set(
      input.indicators
        .filter((indicator) =>
          ["webhook", "telegram"].includes(indicator.type),
        )
        .map((indicator) => indicator.entry),
    ),
  );
  for (const entry of webhookEntries) {
    if (!input.entryIsReachable(entry)) continue;
    add(
      {
        id: "JARLAB-SECRET-001",
        severity: "critical",
        confidence: "high",
        category: "network",
        title: "Embedded exfiltration credential",
        description:
          "A live-looking webhook or bot credential is embedded in the archive and could be used as an exfiltration channel.",
        recommendation:
          "Revoke the credential, block the endpoint, and inspect all callers that send data to it.",
        tags: ["secret", "exfiltration"],
        evidence: input.indicators
          .filter(
            (indicator) =>
              indicator.entry === entry &&
              ["webhook", "telegram"].includes(indicator.type),
          )
          .map((indicator) => indicator.value)
          .slice(0, 4),
      },
      entry,
    );
  }
}

/**
 * `JARLAB-ARCHIVE-001` (zip-slip) and `JARLAB-NATIVE-002` (bundled native
 * code). Both are properties of the entry list, not of any class.
 */
export function emitArchiveFindings(
  input: {
    unsafePaths: string[];
    archiveEntries: ArchiveEntry[];
    referencedByReachableCode: (path: string) => boolean;
  },
  add: RuleSink,
) {
if (input.unsafePaths.length) {
  add(
    {
      id: "JARLAB-ARCHIVE-001",
      severity: "critical",
      confidence: "high",
      category: "archive",
      title: "Archive path traversal",
      description:
        "One or more entries attempt to escape the extraction directory using parent path segments.",
      recommendation:
        "Do not extract the archive with a naive ZIP library. Treat the package as hostile.",
      tags: ["zip-slip", "archive"],
      evidence: input.unsafePaths.slice(0, 4),
    },
    input.unsafePaths[0],
  );
}

for (const entry of input.archiveEntries.filter((item) => item.kind === "native")) {
  if (!input.referencedByReachableCode(entry.path)) continue;
  add(
    {
      id: "JARLAB-NATIVE-002",
      severity: "high",
      confidence: "high",
      category: "structure",
      title: "Bundled native executable",
      description:
        "The archive includes native code that requires separate binary analysis.",
      recommendation:
        "Extract the binary and inspect imports, signatures, and behavior in an isolated environment.",
      tags: ["native", "binary"],
      evidence: [`${entry.path} · ${formatBytes(entry.size)}`],
    },
    entry.path,
  );
}
}

/**
 * `JARLAB-COMPOUND-001` and `-002`: archive-wide pairs. They run after every
 * other rule, because a pair is only meaningful once the whole finding set
 * exists.
 */
export function emitArchiveCorrelations(
  findings: Finding[],
  add: RuleSink,
) {
const behaviorIds = new Set(findings.map((finding) => finding.ruleId));
if (
  behaviorIds.has("JARLAB-CRED-001") &&
  behaviorIds.has("JARLAB-NET-001")
) {
  add(
    {
      id: "JARLAB-COMPOUND-001",
      severity: "high",
      confidence: "medium",
      category: "credentials",
      title: "Archive-wide collection and outbound channel",
      description:
        "Credential-store access and outbound network capability both appear in the archive, but may reside in unrelated components.",
      recommendation:
        "Quarantine the sample and trace the complete collection-to-network data flow.",
      tags: ["compound", "stealer", "exfiltration"],
      evidence: [
        "Credential storage references detected",
        "Outbound network APIs or endpoints detected",
      ],
    },
    "archive-wide correlation",
  );
}
if (
  behaviorIds.has("JARLAB-LOAD-001") &&
  behaviorIds.has("JARLAB-NET-001")
) {
  add(
    {
      id: "JARLAB-COMPOUND-002",
      severity: "high",
      confidence: "medium",
      category: "execution",
      title: "Network-capable dynamic loader",
      description:
        "Network and dynamic class-loading behavior co-occur, which can enable delivery of an unseen second stage.",
      recommendation:
        "Identify the class-byte source and block all untrusted endpoints before further analysis.",
      tags: ["compound", "loader", "second-stage"],
      evidence: [
        "Dynamic bytecode loading detected",
        "Outbound network capability detected",
      ],
    },
    "archive-wide correlation",
  );
}
}

/**
 * `JARLAB-OBF-001`: most class names are one or two characters. Naming, not
 * decryption, so it is a separate rule from the `OBF-005..008` family.
 */
export function emitObfuscationNamingFinding(
  classes: ClassInfo[],
  add: RuleSink,
) {
const shortClassNames = classes.filter(
  (item) => item.simpleName.replace(/\$\d+$/, "").length <= 2,
).length;
if (classes.length >= 20 && shortClassNames / classes.length >= 0.55) {
  add(
    {
      id: "JARLAB-OBF-001",
      severity: "low",
      confidence: "medium",
      category: "evasion",
      title: "Heavily shortened class names",
      description:
        "A large share of class names are one or two characters long, consistent with name obfuscation.",
      recommendation:
        "Use decompilation and behavior evidence rather than class names when tracing execution.",
      tags: ["obfuscation", "naming"],
      evidence: [
        `${shortClassNames} of ${classes.length} classes have shortened names`,
      ],
    },
    "archive-wide structure",
  );
}
}

/**
 * `JARLAB-SIGN-001`: signature metadata with no signature block. Separate from
 * `SIGN-002/003` because it reads the entry list rather than the integrity
 * result, and it was always evaluated later in the scan.
 */
export function emitIncompleteSignatureFinding(
  signatureFiles: string[],
  add: RuleSink,
) {
const sfFiles = signatureFiles.filter((path) => path.endsWith(".SF"));
const blockFiles = signatureFiles.filter((path) =>
  /\.(?:RSA|DSA|EC)$/i.test(path),
);
if (sfFiles.length && !blockFiles.length) {
  add(
    {
      id: "JARLAB-SIGN-001",
      severity: "medium",
      confidence: "medium",
      category: "signature",
      title: "Incomplete JAR signature",
      description:
        "Signature metadata is present without a matching cryptographic signature block.",
      recommendation:
        "Verify the JAR with jarsigner and compare it with a trusted publisher release.",
      tags: ["signature", "integrity"],
      evidence: sfFiles.slice(0, 4),
    },
    "META-INF",
  );
}
}

/**
 * `JARLAB-CONTEXT-001`: the note that down-weights a library-shaped archive.
 * Last, because it asks whether any critical finding was raised at all.
 */
export function emitUnprovenCapabilityNote(
  input: { capabilityProven: boolean; findings: Finding[] },
  add: RuleSink,
) {
if (!input.capabilityProven) {
  const unproven = input.findings.filter((finding) =>
    isCapabilitySurfaceRule(finding.ruleId),
  );
  if (unproven.length) {
    add(
      {
        id: "JARLAB-CONTEXT-001",
        severity: "info",
        confidence: "high",
        category: "execution",
        title: "Capabilities are present but unproven",
        description: `This archive declares no entry point and JarLab proved no data-flow path, so it cannot start itself. ${unproven.length.toLocaleString()} capability finding${unproven.length === 1 ? "" : "s"} are listed below for review but were weighted down in the score, which is normal for a library or dependency.`,
        recommendation:
          "Review the capability findings against whatever code loads this archive. If it is a dependency, judge it in the context of the application that calls it.",
        tags: ["context", "reachability", "library"],
        evidence: Array.from(
          new Set(unproven.map((finding) => finding.title)),
        ).slice(0, 4),
      },
      "archive-wide correlation",
    );
  }
}
}

// ---------------------------------------------------------------------------
// The index.
//
// Every rule id the product can emit, in one list, with a pointer to the
// emitter that produces it. `STATIC_RULE_IDS` is the whole of category one:
// if an id is not here and is not a prefix in `DERIVED_RULE_FAMILIES`, the
// product cannot emit it. That is the property that was missing before, when
// the answer required reading the assembly function.
// ---------------------------------------------------------------------------

/** Category one: fixed ids, defined before the scan runs. */
export const STATIC_RULE_IDS = {
  behavior: BEHAVIOR_RULES.map((rule) => rule.id),
  scan: {
    emitIntegrityFindings: ["JARLAB-SIGN-002", "JARLAB-SIGN-003"],
    emitReputationFinding: ["JARLAB-REPUTATION-MODRINTH"],
    emitStringEncryptionFindings: [
      "JARLAB-OBF-005",
      "JARLAB-OBF-008",
      "JARLAB-OBF-006",
      "JARLAB-OBF-007",
    ],
    emitMasqueradeFinding: ["JARLAB-STRUCT-003"],
    emitEntryCorrelations: [
      "JARLAB-COMPOUND-003",
      "JARLAB-COMPOUND-004",
      "JARLAB-COMPOUND-005",
    ],
    emitExfiltrationFinding: ["JARLAB-SECRET-001"],
    emitArchiveFindings: ["JARLAB-ARCHIVE-001", "JARLAB-NATIVE-002"],
    emitArchiveCorrelations: ["JARLAB-COMPOUND-001", "JARLAB-COMPOUND-002"],
    emitObfuscationNamingFinding: ["JARLAB-OBF-001"],
    emitIncompleteSignatureFinding: ["JARLAB-SIGN-001"],
    emitUnprovenCapabilityNote: ["JARLAB-CONTEXT-001"],
  },
} as const;

/**
 * Category two: ids computed from the observation. The id template is given
 * so a reader can tell what a sample would have to contain to produce one.
 * The logic is in `observed-findings.ts`, named by the same keys.
 */
export const DERIVED_RULE_FAMILIES = {
  emitActionFindings: "JARLAB-ACTION-<ACTION_ID>",
  emitFlowFindings: "JARLAB-FLOW-<SOURCE_KIND>-<SINK_KIND>",
  emitPayloadFindings: "JARLAB-PAYLOAD-<TYPE>",
  emitYaraFindings: "<the analyst's own YARA rule id>",
  emitFingerprintFindings: "JARLAB-FP-<FAMILY>",
  emitKnownHashFindings: "JARLAB-HASH-<FAMILY>",
  emitFamilySignatureFindings: "JARLAB-FAMILY-<SIGNATURE_ID>",
  emitDependencyFindings: "JARLAB-DEP-<ADVISORY_ID>",
} as const;
